/**
 * Products API Client
 * Epic 8: Product Management
 */

import { api } from './client';

// ============================================
// TYPES
// ============================================

export type ProductStatus = 'active' | 'inactive' | 'discontinued';
export type ProductCategory =
  | 'power_tool'
  | 'hand_tool'
  | 'accessory'
  | 'consumable'
  | 'spare_part'
  | 'rental_equipment'
  | 'other';

export interface Supplier {
  id: string;
  name: string;
  code: string;
}

export interface Product {
  id: string;
  status: ProductStatus;
  category: string;
  categoryId?: string;
  sku: string;
  barcode?: string;
  name: string;
  shortName?: string;
  description?: string;
  brand?: string;
  model?: string;
  purchasePrice: number;
  sellingPriceNet: number;
  sellingPriceGross: number;
  vatRate: number;
  marginPercent: number;
  stockQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  minStockLevel: number;
  reorderQuantity: number;
  location?: string;
  supplier?: Supplier;
  lastPurchaseDate?: string;
  lastPurchasePrice?: number;
  rentalDailyRate?: number;
  rentalWeeklyRate?: number;
  rentalMonthlyRate?: number;
  rentalDepositAmount?: number;
  imageUrl?: string;
  thumbnailUrl?: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

export interface ProductListResponse {
  data: Product[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface ProductResponse {
  data: Product;
}

export interface ProductStats {
  total: number;
  active: number;
  lowStock: number;
  outOfStock: number;
  totalValue: number;
}

/**
 * Termék-kategória (`GET /products-direct/categories`).
 *
 * ⚠️ wire-fidelity (mérve dev4, 2026-07-28): a BE **`code`**-ot ad vissza
 * (pl. `CAT-KISGEP`, `ACC-AKKU`), NEM `slug`-ot. A korábbi `slug` mező
 * kontrakt-drift volt — sosem érkezett meg, `undefined`-ként olvasódott volna.
 * A `code` az, amit a `ProductDTO.category` is hordoz
 * (`product-direct.controller.ts` toDTO: `product.category?.code ?? 'other'`),
 * így a lista/részletező badge ezen a mezőn párosít.
 */
export interface CategoryDTO {
  id: string;
  name: string;
  code: string;
  parentId?: string;
}

export interface ProductFilters {
  status?: 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';
  categoryId?: string;
  supplierId?: string;
  search?: string;
  brand?: string;
  lowStock?: boolean;
  page?: number;
  pageSize?: number;
  type?: string | undefined;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of products with filters
 */
export async function getProducts(filters: ProductFilters = {}): Promise<ProductListResponse> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.categoryId) params.append('categoryId', filters.categoryId);
  if (filters.supplierId) params.append('supplierId', filters.supplierId);
  if (filters.search) params.append('search', filters.search);
  if (filters.brand) params.append('brand', filters.brand);
  if (filters.lowStock) params.append('lowStock', 'true');
  if (filters.type) params.append('type', filters.type);
  // M-018 fix: Use !== undefined to avoid falsy-zero bug
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/products-direct?${queryString}` : '/products-direct';

  return api.get<ProductListResponse>(endpoint);
}

/**
 * Get single product by ID
 */
export async function getProductById(id: string): Promise<ProductResponse> {
  return api.get<ProductResponse>(`/products-direct/${id}`);
}

/**
 * Get product statistics
 */
export async function getProductStats(): Promise<{ data: ProductStats }> {
  return api.get<{ data: ProductStats }>('/products-direct/stats');
}

/**
 * Get product categories
 */
export async function getProductCategories(): Promise<{ data: CategoryDTO[] }> {
  return api.get<{ data: CategoryDTO[] }>('/products-direct/categories');
}

/**
 * Get distinct product brands for autocomplete (O-PRD-1)
 */
export async function getProductBrands(): Promise<{ data: string[] }> {
  return api.get<{ data: string[] }>('/products-direct/brands');
}

/**
 * Create new product
 */
/**
 * ADR-129 — Belső cikkszám forrás (Phase 1 Sprint 25 W1).
 *   AUTO_GENERATED — rPI generálta a `KGC-{YYYY}-{seq:04d}` mintán
 *   SUPPLIER_FGS / SUPPLIER_MAV — adott beszállító saját cikkszáma átvéve
 */
export type ProductInternalCodeSource = 'AUTO_GENERATED' | 'SUPPLIER_FGS' | 'SUPPLIER_MAV';

export async function createProduct(data: {
  /** ADR-129 §2.2: `sku` opcionális AUTO_GENERATED-nél (backend auto-gen).
   * SUPPLIER_FGS/MAV esetén KÖTELEZŐ (a beszállítói cikkszám átvétele). */
  sku?: string;
  name: string;
  shortName?: string;
  description?: string;
  categoryId?: string;
  brand?: string;
  model?: string;
  barcode?: string;
  /** ADR-129 Phase 1 — gyári cikkszám (opcionális, NULL ha üres) */
  manufacturerCode?: string;
  /** ADR-129 Phase 1 — internal code forrás (default AUTO_GENERATED) */
  internalCodeSource?: ProductInternalCodeSource;
  purchasePrice?: number;
  sellingPrice?: number;
  vatPercent?: number;
  unit?: string;
  minStockLevel?: number;
  reorderQuantity?: number;
}): Promise<ProductResponse> {
  return api.post<ProductResponse>('/products-direct', data);
}

/**
 * Update product
 */
export async function updateProduct(
  id: string,
  data: {
    status?: 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED';
    name?: string;
    shortName?: string;
    description?: string;
    categoryId?: string;
    brand?: string;
    model?: string;
    barcode?: string;
    purchasePrice?: number;
    sellingPrice?: number;
    vatPercent?: number;
    minStockLevel?: number;
    reorderQuantity?: number;
  }
): Promise<ProductResponse> {
  return api.patch<ProductResponse>(`/products-direct/${id}`, data);
}

/**
 * Delete product (soft delete)
 */
export async function deleteProduct(id: string): Promise<{ success: boolean }> {
  return api.delete<{ success: boolean }>(`/products-direct/${id}`);
}

// ============================================
// SALES-FG-4-5: Exploded View — Product Components (BOM)
// ============================================

export interface ProductComponent {
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface ProductComponentsResponse {
  data: {
    components: ProductComponent[];
    totalBomCost: number;
    sellingPrice: number;
    margin: number;
    marginPercent: number;
  };
}

/**
 * Get product components (BOM - Bill of Materials)
 */
export async function getProductComponents(id: string): Promise<ProductComponentsResponse> {
  return api.get<ProductComponentsResponse>(`/products-direct/${id}/components`);
}

/**
 * Update product components (BOM)
 */
export async function updateProductComponents(
  id: string,
  components: Array<{ productId: string; quantity: number }>
): Promise<{ success: boolean; componentCount: number }> {
  return api.patch<{ success: boolean; componentCount: number }>(
    `/products-direct/${id}/components`,
    { components }
  );
}

// ============================================
// SALES-FG-4-11: Margin Report
// ============================================

export interface MarginReportItem {
  id: string;
  name: string;
  sku?: string;
  revenue: number;
  cogs: number;
  marginAmount: number;
  marginPercent: number;
  quantitySold: number;
}

export interface MarginReportResponse {
  data: {
    items: MarginReportItem[];
    totals: {
      revenue: number;
      cogs: number;
      marginAmount: number;
      marginPercent: number;
    };
    period: { from: string; to: string };
    groupBy: string;
  };
}

/**
 * Get margin report data
 */
export async function getMarginReport(params: {
  from?: string;
  to?: string;
  groupBy?: 'product' | 'category';
}): Promise<MarginReportResponse> {
  const urlParams = new URLSearchParams();
  if (params.from) urlParams.append('from', params.from);
  if (params.to) urlParams.append('to', params.to);
  if (params.groupBy) urlParams.append('groupBy', params.groupBy);
  const qs = urlParams.toString();
  return api.get<MarginReportResponse>(`/products-direct/reports/margin${qs ? `?${qs}` : ''}`);
}

/**
 * Export margin report as CSV
 */
export function getMarginReportExportUrl(params: {
  from?: string;
  to?: string;
  groupBy?: 'product' | 'category';
}): string {
  const urlParams = new URLSearchParams();
  if (params.from) urlParams.append('from', params.from);
  if (params.to) urlParams.append('to', params.to);
  if (params.groupBy) urlParams.append('groupBy', params.groupBy);
  const qs = urlParams.toString();
  return `/api/v1/products-direct/reports/margin/export${qs ? `?${qs}` : ''}`;
}
